import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from 'sonner'
import DisabledReason from './DisabledReason'
import { api } from '../../api/client'
import { auslaufWirktSchon, heuteUtcIso, formatAuslaufTag } from '../../utils/discontinued'

/**
 * DiscontinuedDialog — record that a product is being discontinued by its maker.
 *
 * @description
 *   Writes `PATCH /api/products/<sku>/discontinued`. Discontinued means: keep
 *   selling the stock, stop reordering. It removes the SKU from the reorder
 *   recommendations and from the PO wizard's pre-fill, and it writes **nothing
 *   to Shopify** — that is the endpoint's deliberate design, not an omission.
 *
 *   A date `<= today` takes effect now; a date in the future is an
 *   announcement and only bites on that day. The component tells the operator
 *   which of the two they are about to do, because the two look identical in a
 *   date field and differ by one day.
 *
 *   🔴 This dialog SETS and CHANGES, it never CLEARS. Removing a discontinuation
 *   destroys the note with it and belongs behind the hold-to-confirm control on
 *   the product page (`ProductDetail.jsx`). Two reasons, both load-bearing:
 *   `DeclinedProductsDrawer.jsx` states on the same screen that removal happens
 *   on the product page, and from a reorder-recommendation row the operation is
 *   one-way anyway — a row whose date reaches today disappears from the list,
 *   so there is nothing left to click.
 *
 *   🔴 What gets sent comes from the ARGUMENTS of `speichern`, never from state.
 *   `frontend/src/components/__tests__/DiscontinuedDialog.test.jsx` exists
 *   because a setter called immediately before a save handler does not reach
 *   the closure of the running render — the old value went to the server while
 *   the toast reported success.
 *
 * @useWhen
 *   - An operator wants to stop reordering a SKU from wherever they meet it
 *   - Recording a manufacturer's end-of-life announcement with a lead time
 *   - A row action that must not navigate away from the current list
 *
 * @dontUseFor
 *   - Removing an existing discontinuation — use the product page's HoldButton
 *   - Hiding a product from the shop — that is `product_status` / Shopify
 *   - Suppressing one recommendation for 14 days — that is Decline
 *
 * @related
 *   - HoldButton — the hold-to-confirm control the removal path uses
 *   - DeclinedProductsDrawer — shows what a discontinuation removed from the list
 *
 * @reuseHints
 *   - `ProductDetail.jsx` still carries its own inline copy of this dialog (the
 *     `discModalOpen` block — deliberately no line number: the comment in that
 *     very file records a Gate finding a stale one there TWICE); converting it
 *     is a separate PR and would make this the single definition
 *   - Product list / portfolio table row menus
 *
 * @props
 *   open — controlled visibility
 *   sku — the product to patch; the dialog does not resolve it itself
 *   productName — shown in the copy so the operator sees what they are ending
 *   initialDate — existing `discontinued_at` (`YYYY-MM-DD`) or empty for a new one
 *   initialNote — existing `discontinued_note` or empty
 *   onClose — called on cancel and after a successful save
 *   onSaved — called with the endpoint's answer `{discontinued_at, discontinued_note}`
 */
export default function DiscontinuedDialog({
  open,
  sku,
  productName,
  initialDate,
  initialNote,
  onClose,
  onSaved,
}) {
  const [datum, setDatum] = useState('')
  const [notiz, setNotiz] = useState('')
  const [speichert, setSpeichert] = useState(false)

  // Beim Oeffnen aus den Eigenschaften vorbelegen. Abhaengig von `open` und
  // `sku`, nicht von der Objektidentitaet der Zeile: sonst wuerde jede
  // Neuberechnung der Elternliste die Eingabe des Bedieners zuruecksetzen.
  useEffect(() => {
    if (!open) return
    setDatum(initialDate || heuteUtcIso())
    setNotiz(initialNote || '')
  }, [open, sku]) // eslint-disable-line react-hooks/exhaustive-deps
  // initialDate/initialNote absichtlich draussen: sie kommen aus einem Feld der
  // Elternliste, das sich nach dem Speichern aendert — sie mitzufuehren wuerde
  // die Vorbelegung mitten in der Eingabe umschreiben.

  const wirktSofort = auslaufWirktSchon(datum)

  // 🔴 Die Werte kommen aus den ARGUMENTEN. Siehe Kopfkommentar.
  const speichern = async (d = datum, n = notiz) => {
    if (!d) return
    setSpeichert(true)
    try {
      const { data } = await api.patch(`/api/products/${sku}/discontinued`, {
        discontinued_at: d || null,
        note: (n || '').trim() || null,
      })
      toast.success(
        auslaufWirktSchon(data.discontinued_at)
          ? 'Discontinued — this product will no longer be reordered.'
          // 🔴 Formatiert, nicht roh. Bis zur Regel-16-Runde 3 stand hier das
          // ISO-Datum — der Chip zwei Sekunden vorher sagte „12.03.2027", der
          // Toast „2027-03-12". Dieselbe Handlung, dieselbe Sekunde, zwei
          // Schreibweisen.
          : `Discontinuation announced for ${formatAuslaufTag(data.discontinued_at)}.`
      )
      onSaved?.(data)
      onClose?.()
    } catch (err) {
      console.error('Failed to save discontinued_at:', err)
      toast.error('Could not save the discontinuation.')
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="discontinued-dialog-title"
    >
      <DialogTitle id="discontinued-dialog-title">Discontinuation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          From this date <strong>{productName || sku}</strong> is{' '}
          <strong>no longer reordered</strong> — neither in the recommendations
          nor in the PO wizard's pre-fill. It stays on sale, and nothing changes
          in Shopify.
        </Typography>
        {/* 🔴 Die Folgenzeile steht im `helperText` des Datumsfelds, nicht als
            loses `<Typography>` daneben. Sie ist das EINZIGE Element, das
            Sofortwirkung von Ankuendigung unterscheidet — als losen Absatz
            bekommt sie keine Vorlesesoftware zu fassen (kein
            `aria-describedby`), und das Notizfeld elf Zeilen tiefer machte es
            schon richtig. Zwei benachbarte Felder, zwei Vertraege. Befund des
            Regel-16-Gates.

            `aria-live="polite"` dazu, weil der Text sich beim Tippen im
            Datumsfeld AENDERT: ohne die Ansage wechselt die Folge lautlos.

            ⚠️ Und die Farbe ist bewusst NICHT `warning.main`: gemessen
            3,11:1 auf Weiss bei dieser Schriftgroesse — unter AA. Sachlich
            waere sie ohnehin falsch, denn ein Auslauf zum heutigen Tag ist
            die beabsichtigte Normalhandlung, keine Warnung. Der Unterschied
            traegt ueber den TEXT, nicht ueber die Farbe. */}
        <TextField
          id="discontinued-from"
          label="Discontinued from"
          type="date"
          fullWidth
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          helperText={
            <Box component="span" aria-live="polite" sx={{ display: 'block' }}>
              {datum
                ? (wirktSofort
                    ? `Takes effect on ${formatAuslaufTag(datum)} — that is today or earlier, so this item leaves the recommendations now and can only be undone on the product page.`
                    : `Takes effect on ${formatAuslaufTag(datum)} — a date in the future is an announcement, and the item stays in the recommendations until that day.`)
                : 'Pick the day from which this product is no longer reordered.'}
            </Box>
          }
        />
        <TextField
          label="Note — where did the announcement come from?"
          fullWidth
          multiline
          minRows={2}
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          disabled={!datum}
          helperText={datum
            ? 'Without it, nobody will remember the reason in six months.'
            : 'Enter a date first.'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={speichert}>Cancel</Button>
        <DisabledReason
          reason={speichert
            ? null
            : !datum ? 'Enter a date first.' : null}
        >
          <Button
            variant="contained"
            onClick={() => speichern()}
            disabled={speichert || !datum}
          >
            {speichert ? 'Saving…' : 'Save'}
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

DiscontinuedDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  sku: PropTypes.string,
  productName: PropTypes.string,
  initialDate: PropTypes.string,
  initialNote: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
}
